import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import FileIcon from '@atlaskit/icon/core/file';
import DocumentsIcon from '@atlaskit/icon/core/files';
import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import QuoteIcon from '@atlaskit/icon/core/quotation-mark';

import { EditedDefinitionScope } from '@atlassian/search-ai/curated/types';

const i18n = defineMessages({
	editDefinitionRestrictedToSpaceOption: {
		id: 'contextual-reading-aids.edit.definition.restricted.to.space.option',
		defaultMessage: 'In this space',
		description:
			'The text is shown as a drop-down option when editing a term definition, indicating that the definition will be scoped and visible only within the current space.',
	},
	editDefinitionRestrictedToPageOption: {
		id: 'contextual-reading-aids.edit.definition.restricted.to.page.option',
		defaultMessage: 'On this page',
		description:
			'The text is shown as a drop-down option when editing a term definition, indicating that the definition will be scoped and visible only on the current page.',
	},
	editDefinitionRestrictedToBlogpostOption: {
		id: 'contextual-reading-aids.edit.definition.restricted.to.blogpost.option',
		defaultMessage: 'On this blogpost',
		description:
			'The text is shown as a drop-down option when editing a term definition, indicating that the definition will be scoped and visible only on the current blog post.',
	},
	editDefinitionRestrictedToSiteOption: {
		id: 'contextual-reading-aids.edit.definition.restricted.to.site.option',
		defaultMessage: 'On this site',
		description:
			'The text is shown as a drop-down option when editing a term definition, indicating that the definition will be scoped and visible across the entire site.',
	},
});

export function useDefinitionRestrictionOptions(contentType?: string): {
	label: string;
	value: EditedDefinitionScope;
	icon: React.JSX.Element;
}[] {
	const { formatMessage } = useIntl();

	return useMemo(
		() => [
			{
				label: formatMessage(i18n.editDefinitionRestrictedToSpaceOption),
				value: EditedDefinitionScope.SPACE,
				icon: <DocumentsIcon label="" />,
			},
			contentType === 'blogpost'
				? {
						label: formatMessage(i18n.editDefinitionRestrictedToBlogpostOption),
						value: EditedDefinitionScope.BLOGPOST,
						icon: <QuoteIcon label="" />,
					}
				: {
						label: formatMessage(i18n.editDefinitionRestrictedToPageOption),
						value: EditedDefinitionScope.PAGE,
						icon: <FileIcon label="" />,
					},
			{
				label: formatMessage(i18n.editDefinitionRestrictedToSiteOption),
				value: EditedDefinitionScope.ORGANIZATION,
				icon: <OfficeBuildingIcon label="" />,
			},
		],
		[contentType, formatMessage],
	);
}
